import { Action, AdvancedListConstants } from "@amzn/ask-legal-domain";
import { ReservedFieldKey } from "@amzn/altar-sds-client";

/**
 * Runtime context expression that SDS substitutes with the triggering item's
 * reserved numeric "id" value when the workflow email is sent.
 */
export const ITEM_ID_CONTEXT_EXPRESSION =
  "${context.item.valuesMap['" + ReservedFieldKey.ID + "']?.value}";

/**
 * Item-link marker embedded in the stored email body. We deliberately store
 * ONLY the plain containerId + a `context.*` id expression — never a URL — so
 * the workflow create/update request stays clear of the SDS API Gateway WAF
 * SSRF body rule (which blocks absolute URLs / "localhost"). The email Lambda
 * parses this marker at send time, builds the full deep link from its own
 * configured webURI, renders a proper link, and strips the marker.
 *
 * Stored form:  [[PF_ITEM_LINK:<containerId>|${context.item.valuesMap['id']?.value}]]
 * At send time: [[PF_ITEM_LINK:<containerId>|<resolvedNumericId>]]
 */
function buildItemLinkMarker(containerId: string): string {
  return (
    AdvancedListConstants.ITEM_LINK_MARKER_PREFIX +
    containerId +
    AdvancedListConstants.ITEM_LINK_MARKER_SEPARATOR +
    ITEM_ID_CONTEXT_EXPRESSION +
    AdvancedListConstants.ITEM_LINK_MARKER_SUFFIX
  );
}

/**
 * Stored email bodies are backtick-wrapped JS template literals (see
 * SmartVariableTextInput.convertDisplayTemplateToStorageTemplate). SDS
 * evaluates the whole literal at send time, so the `${context...}` id
 * expression must live INSIDE the backticks to be interpolated. These helpers
 * therefore splice the marker in just before the closing backtick, and remove
 * it symmetrically, while tolerating non-wrapped/legacy bodies.
 */
function splitTemplateWrapper(storedBody: string): { open: string; inner: string; close: string } {
  if (
    storedBody.length >= 2 &&
    storedBody[0] === "`" &&
    storedBody[storedBody.length - 1] === "`"
  ) {
    return { open: "`", inner: storedBody.slice(1, -1), close: "`" };
  }
  return { open: "", inner: storedBody, close: "" };
}

/**
 * Splices the item-link marker into a stored (backtick-wrapped) email body,
 * inside the template literal so SDS interpolates the item id at send time. Any
 * previously-injected marker is removed first (so re-saving doesn't duplicate
 * it). Returns the body unchanged when the containerId is missing (e.g. an
 * unsaved container) — the link is simply omitted in that case.
 */
export function buildBodyWithItemLink(storedBody: string, containerId?: string): string {
  const base = stripItemLink(storedBody);
  if (!containerId) return base;
  const { open, inner, close } = splitTemplateWrapper(base);
  return open + inner + buildItemLinkMarker(containerId) + close;
}

/**
 * Removes any previously-injected item-link marker from a stored body, so the
 * author-visible editor only ever shows the human-authored content. Preserves
 * the backtick template wrapper.
 */
export function stripItemLink(storedBody: string): string {
  if (!storedBody) return storedBody;
  const { open, inner, close } = splitTemplateWrapper(storedBody);
  const start = inner.indexOf(AdvancedListConstants.ITEM_LINK_MARKER_PREFIX);
  if (start === -1) return storedBody;
  const suffixIndex = inner.indexOf(AdvancedListConstants.ITEM_LINK_MARKER_SUFFIX, start);
  if (suffixIndex === -1) return storedBody;
  const end = suffixIndex + AdvancedListConstants.ITEM_LINK_MARKER_SUFFIX.length;
  return open + (inner.slice(0, start) + inner.slice(end)) + close;
}

/**
 * Whether a stored body already carries an injected item-link marker.
 */
export function hasItemLink(storedBody?: string): boolean {
  return !!storedBody && storedBody.includes(AdvancedListConstants.ITEM_LINK_MARKER_PREFIX);
}

export function extractEmailIdsFromAddresses(addresses: string[]): string[] {
  return addresses.map(email => email.split("@")[0]);
}
export function buildStaticEmailAction(
  subject: string,
  body: string,
  emailAliases: string[],
  defaultDomain: string = "amazon.com"
): {
  action: Action.EmailToStaticRecipients;
  actionType: Action.Type;
} {
  const staticEmails = emailAliases.map(email => {
    return email.includes("@") ? email : `${email}@${defaultDomain}`;
  });

  const action = new Action.EmailToStaticRecipients(
    subject.trim(),
    body.trim(),
    staticEmails
  );

  return {
    action,
    actionType: Action.Type.EmailToStaticRecipients
  };
}

